import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ContactForm from '../components/ContactForm';
import GallerySection from '../components/GallerySection';
import { getCdnImage } from '../lib/imageUtils';

export default function ServiceWinter() {
  const serviceItems = [
    {
      title: 'Snerydning af parkeringspladser og tilkørselsveje',
      desc: 'Hurtig og grundig rydning med maskiner og sneplov, så medarbejdere, kunder og leverandører kan færdes sikkert og uhindret.',
      icon: 'fa-solid fa-snowflake',
    },
    {
      title: 'Saltning af parkeringspladser og tilkørselsveje',
      desc: 'Forebyggende og akut glatførebekæmpelse med professionel saltspreder for at minimere risikoen for faldulykker og glat føre.',
      icon: 'fa-solid fa-icicles',
    },
    {
      title: 'Vinterservice for forretninger, virksomheder og institutioner',
      desc: 'Skræddersyede vinteraftaler for lokale forretninger og erhvervsdrivende, der har brug for sikkerhed for tidlig rydning inden åbningstid.',
      icon: 'fa-solid fa-building',
    },
    {
      title: 'Døgnberedskab ved kraftigt snefald',
      desc: 'Vi følger vejrudsigten tæt og rykker automatisk ud, når sneen falder eller temperaturen indikerer glatføre.',
      icon: 'fa-solid fa-cloud-meatball',
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Vinterservice & snerydning - ENTREPRENØR LANG ApS"
        description="Snerydning og saltning af parkeringspladser og tilkørselsveje for forretninger, virksomheder og institutioner på Lolland-Falster."
        keywords="snerydning, saltning, glatførebekæmpelse, vinterservice, parkeringspladser, Øster Ulslev, Nykøbing Falster"
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/642721966-1419273886566903-5689979263480560367-n.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Snerydning og saltning med entreprenørmaskiner"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider"
            >
              <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
              <span>Tilbage til alle ydelser</span>
            </Link>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Vinterservice & snerydning
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed font-medium">
              Effektiv snerydning og saltning for forretninger, virksomheder og institutioner.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow transition-all"
              >
                Kontakt os om en vinteraftale
              </a>
              <a
                href="tel:40309708"
                className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/20 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone mr-2 text-emerald-400" aria-hidden="true" />
                Ring 40 30 97 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
                <i className="fa-solid fa-snowflake text-xs" aria-hidden="true" />
                Vinterberedskab
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Hold dine adgangsveje og p-pladser sikre gennem vinteren
              </h2>
              <p className="text-slate-600 text-base mt-3 leading-relaxed">
                Når frost og sne melder sin ankomst, kan glatte kørebaner og tilisede parkeringsarealer hurtigt skabe farlige situationer eller driftsstop. Med en fast vinterserviceaftale hos ENTREPRENØR LANG ApS slipper du for bekymringerne. Vi overvåger føret og rykker ud med traktor, gummiged med sneplov og saltspredere.
              </p>
            </div>

            {/* Scrape verbatim excerpt */}
            <div className="p-5 rounded-xl bg-slate-100 border-l-4 border-emerald-600 text-slate-700 text-sm leading-relaxed">
              <p className="font-semibold text-slate-900 mb-1">Fra virksomhedens profil:</p>
              <p>
                ”Udover kloak- og drænarbejde har vi også meget saltning og snerydning i vintermånederne ved parkeringsarealer for forretninger og virksomheder.”
              </p>
            </div>

            {/* Service Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {serviceItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visuals - authentic snow equipment */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-613121285-1380287927132166-3828401678356812271-n.jpg', {
                  width: 800,
                  quality: 85,
                })}
                alt="Ladvogn med saltspreder og traktor til snerydning foran landsbykirke"
                className="w-full h-80 object-cover"
              />
              <div className="p-5">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Virksomhedens eget vintermateriel
                </span>
                <p className="text-slate-900 font-bold text-base mt-1">
                  Klar til udrykning i vintermånederne
                </p>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Vores vognpark er specialudrustet med saltspreder og kraftige sneplove.
                </p>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-6 rounded-2xl space-y-3">
              <h4 className="font-bold text-base flex items-center gap-2">
                <i className="fa-solid fa-calendar-check text-emerald-400" aria-hidden="true" />
                Lav en fast aftale inden vinteren rammer
              </h4>
              <p className="text-slate-300 text-xs sm:text-sm">
                Sikre din virksomhed eller butik en fast plads i vores rute, så dine arealer altid er ryddet og saltet til tiden.
              </p>
              <a
                href="tel:40309708"
                className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400 hover:text-emerald-300 pt-1 whitespace-nowrap"
              >
                <span>Ring på 40 30 97 08 for en uforpligtende vintersnak</span>
              </a>
            </div>
          </div>
        </div>

        {/* Dynamic CMS Gallery Section */}
        <GallerySection
          gallerySlug="vinterservice"
          title="Billeder fra vintertjenesten"
          subtitle="Se vores udstyr og materiel i aktion på snedækkede arealer."
        />
      </section>

      {/* Form */}
      <section id="kontakt" className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm defaultService="Vinterservice & snerydning" />
        </div>
      </section>
    </div>
  );
}
